import React, { createContext, useState, useContext, useCallback, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { smoothScrollTo } from '../utils/scrollUtils';
import type { TransitionContextValue } from '../types/context';

const TransitionContext = createContext<TransitionContextValue>({
  isTransitioning: false,
  hasCompletedTransition: false,
  isAnimatingIn: false,
  startTransition: () => {},
  endTransition: () => {},
  startPageAnimation: () => {},
  resetTransitionState: () => {},
  navigateWithTransition: () => {},
});

interface TransitionProviderProps {
  children: ReactNode;
}

export const TransitionProvider: React.FC<TransitionProviderProps> = ({ children }) => {
  // Track three distinct states in our animation flow
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [hasCompletedTransition, setHasCompletedTransition] = useState<boolean>(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState<boolean>(false);

  // Get router navigation and location
  const location = useLocation();
  const navigate = useNavigate();

  // Start the transition with logo animation
  const startTransition = useCallback(() => {
    setIsTransitioning(true);
    setHasCompletedTransition(false);
    setIsAnimatingIn(false);
  }, []);

  // End the logo transition, prepare for page animation
  const endTransition = useCallback(() => {
    setIsTransitioning(false);
    setHasCompletedTransition(true);

    // Small delay before triggering the page animation sequence
    setTimeout(() => {
      setIsAnimatingIn(true);
    }, 100);
  }, []);

  // After page animations complete, reset the animation state
  const startPageAnimation = useCallback(() => {
    setIsAnimatingIn(true);
  }, []);

  // Reset all states (typically not needed but good for cleanup)
  const resetTransitionState = useCallback(() => {
    setIsTransitioning(false);
    setHasCompletedTransition(false);
    setIsAnimatingIn(false);
  }, []);

  // Handle navigation with transition effect
  const navigateWithTransition = useCallback((to: string) => {
    // Get the current pathname
    const currentPathname = location.pathname;

    // Parse the destination to get path and hash components
    let destPath = to;
    let destHash = '';

    if (to.includes('#')) {
      const parts = to.split('#');
      destPath = parts[0] || currentPathname; // If path is empty, use current path
      destHash = parts[1] ? `#${parts[1]}` : '';
    }

    // Same-page hash navigation (just scroll)
    if (destPath === currentPathname && destHash) {
      const id = destHash.replace('#', '');
      smoothScrollTo(id);

      // Update URL without navigation
      window.history.pushState(null, '', `${destPath}${destHash}`);
      return;
    }

    // Different page navigation (with or without hash)
    if (destPath !== currentPathname) {
      // Start transition animation
      startTransition();

      // Optimized transition delay from 500ms to 400ms
      setTimeout(() => {
        // Force scroll to top before navigation
        window.scrollTo(0, 0);

        // Navigate to the new page with the hash if present
        navigate(`${destPath}${destHash}`);

        // Optimized end transition time from 400ms to 300ms
        setTimeout(() => {
          endTransition();
        }, 300);
      }, 400);
    }
  }, [location.pathname, navigate, startTransition, endTransition]);

  // Expose the transition handler globally for non-context components (like NavLink fallback)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.navigateWithTransition = navigateWithTransition;
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.navigateWithTransition;
      }
    };
  }, [navigateWithTransition]);

  const value: TransitionContextValue = {
    isTransitioning,
    hasCompletedTransition,
    isAnimatingIn,
    startTransition,
    endTransition,
    startPageAnimation,
    resetTransitionState,
    navigateWithTransition
  };

  return (
    <TransitionContext.Provider value={value}>
      {children}
    </TransitionContext.Provider>
  );
};

export const useTransition = (): TransitionContextValue => {
  const context = useContext(TransitionContext);
  if (!context) {
    throw new Error('useTransition must be used within a TransitionProvider');
  }
  return context;
};
